import { cond, errorWhen } from '@idemia/functional';
import { inject, Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { filter, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { authActivated, providers, config } from './auth.config';
import { AppLoadingService } from '../app-loading.service';
import EnvService from '../app.env';
import { DialogComponent } from '../design-system/components/dialog/dialog.component';
import { SnackbarComponent } from '../design-system/components/snackbar/snackbar.component';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  appLoading = inject(AppLoadingService);

  env = inject(EnvService);

  private readonly router = inject(Router);

  private readonly oauthService = inject(OAuthService);

  private readonly dialog = inject(MatDialog);

  private readonly matSnackBar = inject(MatSnackBar);

  get isLoggedIn() {
    return authActivated(this.env) ? this.oauthService.hasValidAccessToken() : true;
  }

  get accessToken() {
    return this.oauthService.getAccessToken();
  }

  private storeProvider(): string {
    const providerUrl = new URLSearchParams(window.location.search).get('provider');

    let provider = providerUrl;

    // use case cognito return, we need to retrieve the provider from state OAuth
    if (!provider && this.oauthService.state) {
      try {
        const oauthState = JSON.parse(atob(this.oauthService.state));

        provider = oauthState.provider;
      } catch (e) {
        console.warn('Unable to parse OAuth state', e);
      }
    }

    // historic Fallback
    if (!provider) {
      provider = localStorage.getItem('provider');
    }

    const providersObj = providers(this.env);
    const providerName = providersObj[provider || ''];

    errorWhen(!provider || !providerName, () => {
      this.matSnackBar.openFromComponent(
        SnackbarComponent,
        {
          data: { message: 'PROVIDER_ERROR' },
        }
      );

      return 'Provider not specified in URL, OAuth state or localStorage.';
    });

    localStorage.setItem('provider', provider || '');

    return provider || '';
  }

  async authenticate() {
    const url = window.location.href.replace(
      /http.*?:\/\/.*?\//,
      ''
    );

    if (authActivated(this.env)) {

      this.oauthService.configure(
        config(this.env)
      );

      this.oauthService.events.pipe(
        filter((event) => [
          'token_error',
          'token_refresh_error',
          'silent_refresh_error',
          'token_validation_error',
          'code_error',
        ].includes(event.type)),
        tap((event) =>
          (
            event.type === 'code_error'
              ? () => this.unauthorizedUser()
              : () => this.reConnectUser()
          )()),
      ).subscribe();

      await this.oauthService
        .loadDiscoveryDocumentAndTryLogin();

      // we need to retrieve the provider from URL/state/localStorage
      const provider = this.storeProvider();

      if (!this.isLoggedIn) {

        if (!url.includes('?code')) {
          localStorage.setItem(
            'redirectUrl',
            url
          );
        }

        const state = btoa(
          JSON.stringify({
            provider,
            redirectUrl: url,
          })
        );

        this.oauthService.initCodeFlow(
          state,
          {
            identity_provider:
              providers(this.env)[provider],
          }
        );

        return;
      }

      await this.startSilentRefresh();
    }

    const redirectUrl = localStorage.getItem('redirectUrl') || '';

    if (redirectUrl) {
      localStorage.removeItem('redirectUrl');

      this.router.navigateByUrl(
        redirectUrl.trim()
      );
    }
  }

  private reConnectUser() {
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    this.dialog.open(DialogComponent, {
      width: '30vw',
      data: {
        title: 'SESSION_EXPIRED_TITLE',
        body: 'SESSION_EXPIRED_TEXT',
        confirmation: false,
      },
    }).afterClosed().subscribe(() => {
      this.authenticate();
    });
  }

  private unauthorizedUser() {
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    this.dialog.open(DialogComponent, {
      width: '30vw',
      hasBackdrop: true,
      disableClose: true,
      data: {
        title: 'USER_NOT_AUTHORIZED_TITLE',
        body: 'USER_NOT_AUTHORIZED_TEXT',
        confirmation: false,
      },
    });
  }

  private async startSilentRefresh() {
    const bufferTime = 60000;

    try {
      await cond(this.oauthService.getAccessTokenExpiration() - new Date().getTime() < bufferTime, () => this.oauthService.refreshToken());
    } catch (err) {
      console.error('Silent refresh failed.', err);
    }

    this.oauthService.setupAutomaticSilentRefresh();
  }

  logout() {
    const logoutUrl = [
      `https://${this.env.value('AWS_AUTH_REDIRECT_SIGNIN')}/logout?`,
      `id_token_hint=${this.oauthService.getIdToken()}&`,
      `client_id=${this.oauthService.clientId}&`,
      `redirect_uri=${this.env.value('AWS_AUTH_REDIRECT_SIGNIN')}&response_type=code`,
    ].join('');

    this.oauthService.logOut(false);
    window.location.href = logoutUrl;
  }
}
